/**
 * JobDescriptionService
 *
 * Generates a job description via the `generate-job-description` edge function
 * and exports it as clean plain text, Markdown, and a properly-formatted Word
 * document (US Letter, navy headings, real bullet lists).
 */

import { saveAs } from 'file-saver';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
  BorderStyle, LevelFormat, convertInchesToTwip,
} from 'docx';
import { supabase } from '../lib/supabase';
import { resolveSolicitationNumber } from '../lib/export/coverPageModel';
import { cleanTitle } from '../lib/export/textCleanup';
import { normalizeJobInput, sanitizeJobDescriptionOutput, SEED_ABOUT_US } from '../lib/export/jobDescriptionNormalize';
import type { JobDescription, JobDescriptionInput, WorkArrangement } from '../types/job-description';

export { SEED_ABOUT_US };

const NAVY = '1F4E96';
const INK = '1A1A1A';
const MUTED = '5A6472';
const PAGE_SIZE_LETTER = { width: 12240, height: 15840 };
const BULLET_REF = 'jd-bullets';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function toStr(v: any): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join(', ');
  if (typeof v === 'object') return v.title || v.name || v.text || v.description || v.label || '';
  return String(v);
}
function toList(v: any): string[] {
  if (v == null || v === '') return [];
  return (Array.isArray(v) ? v : [v]).map(toStr).map((s) => s.trim()).filter(Boolean);
}
function sanitize(name: string): string {
  return (name || 'job_description').replace(/[^a-z0-9]/gi, '_').substring(0, 60);
}

function parseMaybeJson(v: any): any {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return null; }
}

// Clean a role title: drop trailing annotations like "(Key Personnel — PWS 12.2)".
function cleanRoleTitle(raw: string): string {
  return cleanTitle(
    toStr(raw)
      .replace(/\s*[\(\[][^)\]]*[\)\]]\s*$/, '')   // trailing (…) or […]
      .replace(/\s*[-–—|:]\s*(key personnel|pws[^,]*)\s*$/i, '')
  ) || 'Position';
}

// Responsibilities that actually belong to THIS role (never the contract blurb).
function roleResponsibilities(role: any): string[] {
  if (Array.isArray(role?.responsibilities)) return toList(role.responsibilities);
  if (Array.isArray(role?.relevantExperience)) {
    return role.relevantExperience.flatMap((e: any) => toList(e?.responsibilities)).slice(0, 8);
  }
  return [];
}

// Pull the proposal's Technical Approach scope + task names for grounding.
function technicalContext(proposalContent: any): { scope?: string; tasks: string[] } {
  const sections = proposalContent?.technicalSections || [];
  for (const s of sections) {
    if (/technical/i.test(toStr(s?.title))) {
      const d = parseMaybeJson(s?.content);
      if (d) {
        const scope = toStr(d.scopeOverview) || toStr(d.methodology?.overallApproach) || undefined;
        const tasks = toList((d.methodology?.tasks || []).map((t: any) => t?.name));
        return { scope, tasks };
      }
    }
  }
  return { tasks: [] };
}

// ----------------------------------------------------------------------------
// Build the edge-function payload from the rendered role + proposal + contract + profile
// ----------------------------------------------------------------------------
export function buildJobDescriptionPayload(args: {
  role: any;            // the rendered key-personnel / key-role card object
  contract: any;
  profile: any;
  proposalContent?: any; // proposal.content_json — supplies technical scope/tasks
  sow?: any;            // optional contract-analysis SOW
  options?: { salaryRange?: string | null; contingentOnAward?: boolean };
}): JobDescriptionInput {
  const { role, contract, profile, proposalContent, sow, options } = args;
  const title = cleanRoleTitle(role?.title || role?.role || role?.positionTitle);
  const minimumQualifications = toList(role?.qualifications ?? role?.requirements ?? role?.minimumQualifications);
  const isKey = Boolean(role?.required || role?.keyPersonnel || role?.isKeyPersonnel) ||
    /key|manager|lead|principal|director/i.test(title);

  const placeOfPerformance = toStr(sow?.place_of_performance) ||
    [contract?.placeOfPerformance?.city, contract?.placeOfPerformance?.state].filter(Boolean).join(', ');

  const setAsides = Array.isArray(contract?.setAsides) ? contract.setAsides
    : Array.isArray(contract?.set_asides) ? contract.set_asides : [];

  // Responsibilities: this role's own first, then technical task names. Never the
  // SAM notice/amendment description (which is administrative boilerplate).
  const tech = technicalContext(proposalContent);
  const relevantTasks = [...roleResponsibilities(role), ...tech.tasks].slice(0, 12);
  const scopeSummary = toStr(sow?.scope) || tech.scope || undefined;

  // Lane 1: deterministic cleanup (agency normalize, ID strip, acronym expand)
  // before the constrained AI translation.
  return normalizeJobInput({
    role: {
      positionTitle: title,
      minimumQualifications,
      isKeyPersonnel: isKey,
      requiredCertifications: toList(role?.certifications),
      yearsExperience: toStr(role?.experience || role?.yearsExperience) || undefined,
      education: toStr(role?.education || role?.educationLevel) || undefined,
    },
    solicitation: {
      solicitationNumber: resolveSolicitationNumber(contract) || '',
      agency: toStr(contract?.agency),
      title: cleanTitle(toStr(contract?.title)),
      scopeSummary,
      relevantTasks,
      placeOfPerformance: placeOfPerformance || undefined,
      workArrangement: 'Unspecified' as WorkArrangement,
      backgroundTier: toStr(role?.clearance || role?.clearanceRequired) || undefined,
      tools: toList(contract?.tools),
      setAside: setAsides.map(toStr).filter(Boolean).join(', ') || undefined,
    },
    profile: {
      // Profile data BYPASSES the generative lane. Only the company name (factual,
      // used in the position summary) and location (factual) are passed. About Us
      // and value proposition are pulled verbatim post-AI (see profileVerbatimFields)
      // so the model can never fabricate company claims.
      company_name: toStr(profile?.company_name) || 'Our Company',
      value_proposition: undefined,
      qualified_set_asides: toList(profile?.qualified_set_asides),
      city: toStr(profile?.city || profile?.business_address?.city) || undefined,
      state: toStr(profile?.state || profile?.business_address?.state) || undefined,
    },
    options: {
      salaryRange: options?.salaryRange ?? null,
      contingentOnAward: options?.contingentOnAward ?? true,
    },
  });
}

// Verbatim profile boilerplate — pulled straight into the posting, NEVER routed
// through the Lane 2 generative call (so company claims can't be fabricated).
export function profileVerbatimFields(profile: any): { companyOverview: string; eeoStatement: string } {
  const company = toStr(profile?.company_name) || 'Our company';
  const aboutUs = toStr(profile?.about_us) || SEED_ABOUT_US;
  const setAsides = toList(profile?.qualified_set_asides);
  const sdvosb = setAsides.some((s) => /sdvosb|veteran/i.test(s));
  const eeoStatement =
    `${company} is an Equal Opportunity Employer. All qualified applicants will receive ` +
    `consideration for employment without regard to race, color, religion, sex, national ` +
    `origin, disability, or protected veteran status.` +
    (sdvosb ? ' As a Service-Disabled Veteran-Owned Small Business, we strongly encourage veterans to apply.' : '');
  return { companyOverview: aboutUs, eeoStatement };
}

// ----------------------------------------------------------------------------
// Generate
// ----------------------------------------------------------------------------
export async function generateJobDescription(
  payload: JobDescriptionInput,
  verbatim?: { companyOverview: string; eeoStatement: string }
): Promise<JobDescription> {
  const body = JSON.stringify(payload);
  // The function can take ~20s; mobile networks occasionally abort the request at
  // the transport layer (FunctionsFetchError: "Failed to send a request to the
  // Edge Function"). Generation is side-effect-free, so retry that case once.
  const invoke = () => supabase.functions.invoke('generate-job-description', { body });
  let { data, error } = await invoke();
  if (error && /failed to send a request|fetch|network/i.test(error.message || '')) {
    ({ data, error } = await invoke());
  }
  if (error) throw new Error(error.message || 'Job description generation failed');
  // Lane 1b: deterministic guard over the AI output — strip residual refs, dedupe,
  // expand acronyms, and route duties vs. program scope.
  const jd = sanitizeJobDescriptionOutput(data as JobDescription, payload.solicitation?.solicitationNumber);
  // Profile boilerplate is verbatim, not model-generated.
  if (verbatim) {
    jd.companyOverview = verbatim.companyOverview;
    jd.eeoStatement = verbatim.eeoStatement;
  }
  return jd;
}

// ----------------------------------------------------------------------------
// Plain text (board-safe — no markdown symbols)
// ----------------------------------------------------------------------------
export function jobDescriptionToPlainText(jd: JobDescription): string {
  const L: string[] = [];
  const rq = jd.requiredQualifications || ({} as JobDescription['requiredQualifications']);
  const loc = [jd.location?.city, jd.location?.state].filter(Boolean).join(', ');

  L.push(jd.jobTitle || 'Job Description');
  if (jd.contingentOnAward) L.push('(Contingent upon contract award)');
  L.push('');
  const metaBits = [loc, jd.location?.workArrangement, jd.employmentType].filter((b) => b && b !== 'Unspecified');
  if (metaBits.length) L.push(metaBits.join('  |  '));
  if (jd.salaryRange) L.push(`Salary: ${jd.salaryRange}`);
  L.push('');

  const section = (title: string, body?: string) => {
    if (!body) return;
    L.push(title.toUpperCase(), body, '');
  };
  const bulletSection = (title: string, items?: string[]) => {
    const list = (items || []).filter(Boolean);
    if (!list.length) return;
    L.push(title.toUpperCase());
    list.forEach((i) => L.push(`• ${i}`));
    L.push('');
  };

  section('About Us', jd.companyOverview);
  section('Position Summary', jd.positionSummary);
  bulletSection('Key Responsibilities', jd.keyResponsibilities);
  bulletSection('Program Scope', jd.programScope);

  const reqLines: string[] = [];
  if (rq.education) reqLines.push(`Education: ${rq.education}`);
  if (rq.yearsExperience) reqLines.push(`Experience: ${rq.yearsExperience}`);
  if ((rq.requiredCertifications || []).length) reqLines.push(`Certifications: ${rq.requiredCertifications.join(', ')}`);
  if (rq.clearanceOrBackground) reqLines.push(`Background/Clearance: ${rq.clearanceOrBackground}`);
  if (reqLines.length || (rq.requiredSkills || []).length) {
    L.push('REQUIRED QUALIFICATIONS');
    reqLines.forEach((r) => L.push(r));
    (rq.requiredSkills || []).forEach((s) => L.push(`• ${s}`));
    L.push('');
  }

  bulletSection('Preferred Qualifications', jd.preferredQualifications);
  section('Travel', jd.travel);
  section('Equal Opportunity', jd.eeoStatement);
  section('How to Apply', jd.howToApply);

  return L.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// ----------------------------------------------------------------------------
// Markdown
// ----------------------------------------------------------------------------
export function jobDescriptionToMarkdown(jd: JobDescription): string {
  const rq = jd.requiredQualifications || ({} as JobDescription['requiredQualifications']);
  const loc = [jd.location?.city, jd.location?.state].filter(Boolean).join(', ');
  const L: string[] = [];
  L.push(`# ${jd.jobTitle || 'Job Description'}`);
  if (jd.contingentOnAward) L.push('> **Contingent upon contract award**');
  const metaBits = [loc, jd.location?.workArrangement, jd.employmentType].filter((b) => b && b !== 'Unspecified');
  if (metaBits.length) L.push(`*${metaBits.join(' · ')}*`);
  if (jd.salaryRange) L.push(`**Salary:** ${jd.salaryRange}`);
  L.push('');
  const sec = (t: string, b?: string) => { if (b) L.push(`## ${t}`, '', b, ''); };
  const bul = (t: string, items?: string[]) => {
    const list = (items || []).filter(Boolean);
    if (!list.length) return;
    L.push(`## ${t}`, '');
    list.forEach((i) => L.push(`- ${i}`));
    L.push('');
  };
  sec('About Us', jd.companyOverview);
  sec('Position Summary', jd.positionSummary);
  bul('Key Responsibilities', jd.keyResponsibilities);
  bul('Program Scope', jd.programScope);
  L.push('## Required Qualifications', '');
  if (rq.education) L.push(`- **Education:** ${rq.education}`);
  if (rq.yearsExperience) L.push(`- **Experience:** ${rq.yearsExperience}`);
  if ((rq.requiredCertifications || []).length) L.push(`- **Certifications:** ${rq.requiredCertifications.join(', ')}`);
  if (rq.clearanceOrBackground) L.push(`- **Background/Clearance:** ${rq.clearanceOrBackground}`);
  (rq.requiredSkills || []).forEach((s) => L.push(`- ${s}`));
  L.push('');
  bul('Preferred Qualifications', jd.preferredQualifications);
  sec('Travel', jd.travel);
  sec('Equal Opportunity', jd.eeoStatement);
  sec('How to Apply', jd.howToApply);
  return L.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

export function exportJobDescriptionMarkdown(jd: JobDescription): void {
  const blob = new Blob([jobDescriptionToMarkdown(jd)], { type: 'text/markdown;charset=utf-8' });
  saveAs(blob, `${sanitize(jd.jobTitle)}_JD.md`);
}

// ----------------------------------------------------------------------------
// Word (.docx) — clean, properly formatted
// ----------------------------------------------------------------------------
function h2(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 80 },
    children: [new TextRun({ text, bold: true, size: 26, color: NAVY })],
  });
}
function body(text: string): Paragraph {
  return new Paragraph({
    spacing: { after: 120, line: 276 },
    children: [new TextRun({ text, size: 22, color: INK })],
  });
}
function bullet(text: string): Paragraph {
  return new Paragraph({
    numbering: { reference: BULLET_REF, level: 0 },
    spacing: { after: 60, line: 276 },
    children: [new TextRun({ text, size: 22, color: INK })],
  });
}
function labeled(label: string, value: string): Paragraph {
  return new Paragraph({
    spacing: { after: 60, line: 276 },
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 22, color: INK }),
      new TextRun({ text: value, size: 22, color: INK }),
    ],
  });
}

export async function exportJobDescriptionWord(jd: JobDescription): Promise<void> {
  const rq = jd.requiredQualifications || ({} as JobDescription['requiredQualifications']);
  const loc = [jd.location?.city, jd.location?.state].filter(Boolean).join(', ');
  const children: Paragraph[] = [];

  // Title
  children.push(new Paragraph({
    spacing: { after: 60 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: NAVY } },
    children: [new TextRun({ text: jd.jobTitle || 'Job Description', bold: true, size: 36, color: NAVY })],
  }));
  if (jd.contingentOnAward) {
    children.push(new Paragraph({
      spacing: { after: 80 },
      children: [new TextRun({ text: 'Contingent upon contract award', italics: true, size: 20, color: MUTED })],
    }));
  }
  const metaBits = [loc, jd.location?.workArrangement, jd.employmentType].filter((b) => b && b !== 'Unspecified');
  if (metaBits.length) {
    children.push(new Paragraph({
      spacing: { after: 40 },
      children: [new TextRun({ text: metaBits.join('   •   '), size: 22, color: MUTED })],
    }));
  }
  if (jd.salaryRange) children.push(labeled('Salary', jd.salaryRange));

  const sec = (t: string, b?: string) => { if (b) { children.push(h2(t)); children.push(body(b)); } };
  const bul = (t: string, items?: string[]) => {
    const list = (items || []).filter(Boolean);
    if (!list.length) return;
    children.push(h2(t));
    list.forEach((i) => children.push(bullet(i)));
  };

  sec('About Us', jd.companyOverview);
  sec('Position Summary', jd.positionSummary);
  bul('Key Responsibilities', jd.keyResponsibilities);
  bul('Program Scope', jd.programScope);

  // Required Qualifications
  children.push(h2('Required Qualifications'));
  if (rq.education) children.push(labeled('Education', rq.education));
  if (rq.yearsExperience) children.push(labeled('Experience', rq.yearsExperience));
  if ((rq.requiredCertifications || []).length) children.push(labeled('Certifications', rq.requiredCertifications.join(', ')));
  if (rq.clearanceOrBackground) children.push(labeled('Background / Clearance', rq.clearanceOrBackground));
  (rq.requiredSkills || []).forEach((s) => children.push(bullet(s)));

  bul('Preferred Qualifications', jd.preferredQualifications);
  sec('Travel', jd.travel);
  sec('Equal Opportunity Employer', jd.eeoStatement);
  sec('How to Apply', jd.howToApply);

  const doc = new Document({
    creator: 'BidWin',
    title: jd.jobTitle || 'Job Description',
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 22 } },
        heading2: { run: { font: 'Calibri', size: 26, bold: true, color: NAVY }, paragraph: { spacing: { before: 240, after: 80 } } },
      },
    },
    numbering: {
      config: [{
        reference: BULLET_REF,
        levels: [{
          level: 0, format: LevelFormat.BULLET, text: '•', alignment: AlignmentType.LEFT,
          style: { run: { color: NAVY }, paragraph: { indent: { left: convertInchesToTwip(0.35), hanging: convertInchesToTwip(0.2) } } },
        }],
      }],
    },
    sections: [{
      properties: {
        page: {
          size: PAGE_SIZE_LETTER,
          margin: { top: convertInchesToTwip(1), right: convertInchesToTwip(1), bottom: convertInchesToTwip(1), left: convertInchesToTwip(1) },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${sanitize(jd.jobTitle)}_JD.docx`);
}
